import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { Page, PageHeader, StatusBadge } from "@/components/Bits";
import { toast } from "sonner";
import { Play, X, FilePdf, Lightning, PencilSimple, ArrowUUpLeft, MagnifyingGlass, Plus, MicrosoftExcelLogo, Pause, ProhibitInset, ChatCircleText, Trash, ArrowCounterClockwise } from "@phosphor-icons/react";
import DeferredSuggestions from "@/components/DeferredSuggestions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CreateAdjustmentModal from "@/components/CreateAdjustmentModal";
import { ColumnsToggle, useColumnVisibility } from "@/components/ColumnsToggle";
import RecurringAccruals from "@/components/RecurringAccruals";
import AccrualAccuracy from "@/components/AccrualAccuracy";
import ReviewNotes from "@/components/ReviewNotes";
import { XeroLink } from "@/components/XeroLink";
import ExplainMovement from "@/components/ExplainMovement";
import PLSplitEditor from "@/components/PLSplitEditor";

const TYPE_LABELS = { PREPAYMENT: "Prepayments", ACCRUAL: "Accruals", DEFERRED_INCOME: "Deferred Income", DEPRECIATION: "Depreciation" };
const STD_LABEL = { FRS102: "FRS 102", FRS105: "FRS 105" };

function fmtRemovedAt(iso) {
  try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function StandardBadge({ value, testid }) {
  if (!value) return <span data-testid={testid} className="text-xs text-slate-300">Untagged</span>;
  return (
    <span data-testid={testid} className="text-[0.62rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-navy/10 text-navy">
      {STD_LABEL[value] || value}
    </span>
  );
}

const REG_COLUMNS = [
  { key: "bs_code", label: "BS Code" },
  { key: "bs_account", label: "BS Account" },
  { key: "pl_code", label: "P&L Code" },
  { key: "pl_account", label: "P&L Account" },
  { key: "standard", label: "Standard" },
  { key: "next_journal", label: "Next Journal" },
  { key: "final_date", label: "Final Date" },
];
const REG_DEFAULTS = { bs_code: true, bs_account: true, pl_code: true, pl_account: true, standard: true, next_journal: true, final_date: true };

export default function Register({ lockedType = null }) {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showRun, setShowRun] = useState(false);
  const [detail, setDetail] = useState(null);
  const [cols, toggleCol] = useColumnVisibility("register-columns", REG_DEFAULTS);
  const [filter, setFilter] = useState(() => {
    const s = params.get("status");
    return ["Scheduled", "Active", "Completed"].includes(s) ? s : "All";
  });
  const [typeFilter, setTypeFilter] = useState(() => lockedType || params.get("type") || "All");
  const [standardFilter, setStandardFilter] = useState(() => {
    const s = params.get("standard");
    return ["FRS102", "FRS105", "Untagged"].includes(s) ? s : "All";
  });
  const [pdfing, setPdfing] = useState(false);
  const [exceling, setExceling] = useState(false);
  const [groupByAccount, setGroupByAccount] = useState(false);
  const [search, setSearch] = useState(() => params.get("code") || params.get("q") || "");
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [releaseBusy, setReleaseBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [removed, setRemoved] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [noteCounts, setNoteCounts] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  const openRelease = (e, row) => {
    e.stopPropagation();
    setReleaseDate(new Date().toISOString().slice(0, 10));
    setReleaseTarget(row);
  };

  const doReverse = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.schedule_id;
    setDeleteBusy(true);
    try {
      const { data } = await api.post(`/schedules/${id}/reverse`, {});
      toast.success(`Reversed in Xero — ${data.reversed || 0} reversing journal(s) posted so Xero matches the register.`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reverse this line in Xero");
    } finally { setDeleteBusy(false); }
  };

  const openDelete = (e, row) => {
    e.stopPropagation();
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget.schedule_id;
    const isManual = deleteTarget.manual;
    setDeleteBusy(true);
    try {
      const { data } = await api.delete(`/schedules/${id}`);
      toast.success(isManual
        ? "Manual adjustment deleted."
        : "Line deleted — the imported item was returned to the Unscheduled queue.",
        { action: { label: "Undo", onClick: () => restore(id) } });
      if (data?.ghost_pending > 0) {
        toast.warning(
          `${data.ghost_pending} pending journal${data.ghost_pending === 1 ? "" : "s"} in this organisation ${data.ghost_pending === 1 ? "is" : "are"} no longer linked to a live register line and won't post. Run the Journal integrity check on the Reconciliation page to review and clear them.`,
          { duration: 12000 });
      }
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete this line");
    } finally { setDeleteBusy(false); }
  };

  const confirmRelease = async () => {
    if (!releaseTarget) return;
    setReleaseBusy(true);
    try {
      const { data } = await api.post(`/schedules/${releaseTarget.schedule_id}/release-full`, { date: releaseDate });
      toast.success(`Full release of ${money(data.remaining, releaseTarget.currency)} scheduled for ${fmtDate(data.date)}`);
      setReleaseTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule full release");
    } finally { setReleaseBusy(false); }
  };

  const setStatusFilter = (f) => {
    setFilter(f);
    const next = new URLSearchParams(params);
    f === "All" ? next.delete("status") : next.set("status", f);
    setParams(next, { replace: true });
  };

  const setType = (t) => {
    setTypeFilter(t);
    const next = new URLSearchParams(params);
    t === "All" ? next.delete("type") : next.set("type", t);
    setParams(next, { replace: true });
  };

  const setStandard = (s) => {
    setStandardFilter(s);
    const next = new URLSearchParams(params);
    s === "All" ? next.delete("standard") : next.set("standard", s);
    setParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/register");
      setRows(data.register);
      api.get("/comments/counts?entity_type=schedule")
        .then(({ data }) => setNoteCounts(data.counts || {})).catch(() => {});
      api.get("/schedules/recently-removed")
        .then(({ data }) => setRemoved(data.removed || [])).catch(() => {});
    } finally { setLoading(false); }
    window.dispatchEvent(new Event("badges:refresh"));
    setRefreshKey((k) => k + 1);
  }, []);

  const restore = async (id) => {
    try {
      await api.post(`/schedules/${id}/restore`);
      toast.success("Line restored");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not restore this line");
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const openId = params.get("open");
    if (openId) openDetail(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runDaily = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/scheduler/run");
      if (data.queued > 0)
        toast.success(`${data.queued} journal(s) sent for approval${data.posted ? `; posted ${data.posted}` : ""}`);
      else
        toast.success(`Posted ${data.posted} due journal(s) to Xero`);
      setShowRun(false);
      load();
    } finally { setRunning(false); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/schedules/${id}`);
    setDetail(data);
  };

  const downloadPdf = async () => {
    setPdfing(true);
    try {
      const res = await api.get(`/register/pdf?status=${encodeURIComponent(filter)}&type=${encodeURIComponent(typeFilter)}&standard=${encodeURIComponent(standardFilter)}${groupByAccount ? "&group_by=account" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const typeSlug = typeFilter === "All" ? "" : `-${TYPE_LABELS[typeFilter].toLowerCase().replace(/\s+/g, "-")}`;
      a.download = `accruals-prepayments-register${typeSlug}-${filter.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Register PDF downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally { setPdfing(false); }
  };

  const downloadExcel = async () => {
    setExceling(true);
    try {
      const res = await api.get(`/register/excel?status=${encodeURIComponent(filter)}&type=${encodeURIComponent(typeFilter)}&standard=${encodeURIComponent(standardFilter)}${groupByAccount ? "&group_by=account" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `accruals-prepayments-register-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Register Excel downloaded");
    } catch {
      toast.error("Could not generate Excel");
    } finally { setExceling(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = rows.filter((r) =>
    (filter === "All" || r.status === filter) &&
    (typeFilter === "All" || r.category === typeFilter) &&
    (standardFilter === "All" ||
      (standardFilter === "Untagged" ? !r.compliance : r.compliance === standardFilter)) &&
    (!q ||
      (r.doc_number || "").toLowerCase().includes(q) ||
      (r.contact || "").toLowerCase().includes(q) ||
      (r.source_account_code || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)))
    // Default order: soonest "Next Journal" date first; schedules with no upcoming journal last.
    .sort((a, b) => {
      const da = a.next_journal_date || "";
      const db = b.next_journal_date || "";
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });

  const totals = filtered.reduce((a, r) => ({
    original: a.original + (r.original_amount || 0),
    released: a.released + (r.released_amount || 0),
    remaining: a.remaining + (r.remaining_balance || 0),
  }), { original: 0, released: 0, remaining: 0 });
  const totalsCcy = filtered[0]?.currency;
  const leadCols = 3 + [cols.standard, cols.bs_code, cols.bs_account, cols.pl_code, cols.pl_account].filter(Boolean).length;
  const trailCols = 1 + [cols.next_journal, cols.final_date].filter(Boolean).length; // status + optional
  const totalCols = leadCols + 3 + trailCols + 1; // + 3 money cols + actions

  // On-screen grouping by balance-sheet account code (ties to the "Group by account code" toggle).
  const accountGroups = (() => {
    if (!groupByAccount) return [];
    const by = {};
    filtered.forEach((r) => {
      const code = r.source_account_code || "—";
      (by[code] ||= { code, name: r.source_account_name || code, rows: [], original: 0, released: 0, remaining: 0, ccy: r.currency }).rows.push(r);
      by[code].original += r.original_amount || 0;
      by[code].released += r.released_amount || 0;
      by[code].remaining += r.remaining_balance || 0;
    });
    return Object.values(by).sort((a, b) => String(a.code).localeCompare(String(b.code)));
  })();

  return (
    <Page>
      <PageHeader overline="Live register" title={lockedType ? `${TYPE_LABELS[lockedType]} Register` : "Accruals & Prepayments Register"}
        subtitle="Original, released and remaining balances with the next journal date and status.">
        <button onClick={() => setShowCreate(true)} data-testid="new-adjustment-btn"
          className="flex items-center gap-2 bg-white border border-orange/40 text-orangeink px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange hover:text-white transition-colors duration-200">
          <Plus size={18} weight="bold" /> New adjustment
        </button>
        <button onClick={downloadPdf} disabled={pdfing} data-testid="download-pdf-btn"
          className="flex items-center gap-2 bg-white border border-slate-200 text-navy px-5 py-2.5 rounded-lg text-sm font-medium hover:border-navy hover:bg-navy/5 transition-colors duration-200 disabled:opacity-60">
          <FilePdf size={18} /> {pdfing ? "BUILDING…" : `PDF${filter !== "All" ? ` · ${filter}` : ""}`}
        </button>
        <button onClick={downloadExcel} disabled={exceling} data-testid="download-excel-btn"
          className="flex items-center gap-2 bg-white border border-positive/40 text-positive px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-positive hover:text-white transition-colors duration-200 disabled:opacity-60">
          <MicrosoftExcelLogo size={18} /> {exceling ? "BUILDING…" : "Excel"}
        </button>
        <button onClick={() => setShowRun(true)} disabled={running} data-testid="run-daily-btn"
          className="flex items-center gap-2 bg-navy text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-orange transition-colors duration-200 disabled:opacity-60">
          <Play size={16} weight="fill" /> {running ? "POSTING…" : "Run daily posting now"}
        </button>
        <ColumnsToggle columns={REG_COLUMNS} visible={cols} onToggle={toggleCol} />
      </PageHeader>

      {lockedType === "ACCRUAL" && <RecurringAccruals onPosted={load} />}
      {lockedType === "ACCRUAL" && <AccrualAccuracy refreshKey={refreshKey} />}
      {lockedType === "DEFERRED_INCOME" && <DeferredSuggestions onConverted={load} />}

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4">
        {!lockedType && (
          <div className="flex items-center gap-2">
            <span className="overline text-slate-600 mr-1">Type</span>
            {[["All", "All"], ["PREPAYMENT", "Prepayments"], ["ACCRUAL", "Accruals"], ["DEFERRED_INCOME", "Deferred Income"], ["DEPRECIATION", "Depreciation"]].map(([v, lbl]) => (
              <button key={v} onClick={() => setType(v)} data-testid={`type-filter-${v}`}
                className={`px-3.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  typeFilter === v ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}>{lbl}</button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="overline text-slate-600 mr-1">Status</span>
          {["All", "Scheduled", "Active", "Completed"].map((f) => (
            <button key={f} onClick={() => setStatusFilter(f)} data-testid={`filter-${f}`}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                filter === f ? "bg-orange text-white border-orange" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>{f}</button>
          ))}
        </div>
        <div className="flex items-center gap-2" data-testid="standard-filter-group">
          <span className="overline text-slate-600 mr-1">Standard</span>
          {[["All", "All"], ["FRS102", "FRS 102"], ["FRS105", "FRS 105"], ["Untagged", "Untagged"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setStandard(v)} data-testid={`standard-filter-${v}`}
              className={`px-3.5 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                standardFilter === v ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}>{lbl}</button>
          ))}
        </div>
        <label data-testid="group-by-account-toggle"
          className={`flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${groupByAccount ? "bg-orange/10 border-orange text-orangeink" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"}`}
          title="Group the register and its PDF & Excel reports by balance-sheet account code, with a balance per Xero account">
          <input type="checkbox" checked={groupByAccount} onChange={(e) => setGroupByAccount(e.target.checked)}
            data-testid="group-by-account-checkbox" className="accent-orange w-3.5 h-3.5" />
          Group by account code
        </label>
        <div className="relative ml-auto w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/35" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="register-search"
            placeholder="Search reference, contact or description…"
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-8 py-1.5 text-xs text-navy placeholder:text-navy/35 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-colors" />
          {search && (
            <button onClick={() => setSearch("")} data-testid="register-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/35 hover:text-navy">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {removed.length > 0 && (
        <div className="mb-4 rounded-xl border border-slate-200 bg-navy/[0.02] px-4 py-3" data-testid="recently-removed-strip">
          <div className="flex items-center gap-2 mb-2">
            <ArrowCounterClockwise size={15} className="text-navy/70" />
            <span className="overline text-navy/70">Recently removed · last 24h</span>
          </div>
          <div className="flex flex-col gap-2">
            {removed.map((r) => (
              <div key={r.schedule_id} data-testid={`removed-${r.schedule_id}`}
                className="flex items-center justify-between gap-3 text-sm bg-white border border-slate-100 rounded-lg px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate">
                    <span className="font-medium text-navy">{r.doc_number}</span>
                    <span className="text-navy/65"> · {r.contact}</span>
                    <span className="ml-2 text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-navy/5 text-navy/70">{TYPE_LABELS[r.category] || r.category}</span>
                    <span className="ml-2 font-mono text-navy/70">{money(r.original_amount, r.currency)}</span>
                    {r.kind === "reversed" && (
                      <span className="ml-2 text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warn/10 text-warn">
                        Reversed in Xero{r.reversed_count ? ` · ${r.reversed_count} jnl` : ""}
                      </span>
                    )}
                    {r.kind === "retired" && (
                      <span data-testid={`removed-retired-badge-${r.schedule_id}`} className="ml-2 text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-negative/10 text-negative">
                        Retired · superseded
                      </span>
                    )}
                  </div>
                  <div className="text-[0.7rem] text-navy/55 mt-0.5" data-testid={`removed-meta-${r.schedule_id}`}>
                    {r.kind === "retired" ? "Retired" : r.kind === "reversed" ? "Reversed" : "Deleted"} by <span className="font-medium text-navy/70">{r.deleted_by || "user"}</span>
                    {r.deleted_at && <> · {fmtRemovedAt(r.deleted_at)}</>}
                  </div>
                </div>
                {r.kind === "reversed" ? (
                  <Link to="/audit" data-testid={`removed-audit-${r.schedule_id}`}
                    title="Reversed in Xero — see the reversing journals in Audit history (can't auto-undo)"
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy/70 hover:bg-navy/5 transition-colors">
                    <ArrowUUpLeft size={13} /> View in audit
                  </Link>
                ) : (
                  <button onClick={() => restore(r.schedule_id)} data-testid={`restore-${r.schedule_id}`}
                    className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange/40 text-orangeink hover:bg-orange hover:text-white transition-colors">
                    <ArrowCounterClockwise size={13} /> Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto">
        <table className="w-full text-sm min-w-[1600px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <Th>Reference</Th><Th>Description</Th><Th>Type</Th>
              {cols.standard && <Th>Standard</Th>}
              {cols.bs_code && <Th>BS Code</Th>}{cols.bs_account && <Th>BS Account</Th>}
              {cols.pl_code && <Th>P&L Code</Th>}{cols.pl_account && <Th>P&L Account</Th>}
              <Th right>Original</Th><Th right>Released</Th><Th right>Remaining</Th>
              {cols.next_journal && <Th>Next Journal</Th>}{cols.final_date && <Th>Final Date</Th>}<Th>Status</Th><Th right>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {(() => {
            const renderRow = (r) => (
              <tr key={r.schedule_id} data-testid={`register-row-${r.schedule_id}`}
                onClick={() => openDetail(r.schedule_id)}
                className="border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors">
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy">{r.doc_number}</span>
                    {r.manual && (
                      <span data-testid={`manual-badge-${r.schedule_id}`} title={r.created_by ? `Created by ${r.created_by}` : "Created in-app"}
                        className="text-[0.6rem] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-orange/10 text-orangeink border border-orange/30">
                        Manual
                      </span>
                    )}
                    {noteCounts[r.schedule_id] > 0 && (
                      <span data-testid={`notes-badge-${r.schedule_id}`} title={`${noteCounts[r.schedule_id]} review note(s)`}
                        className="inline-flex items-center gap-0.5 text-[0.6rem] font-semibold px-1.5 py-0.5 rounded-md bg-navy/5 text-navy/70 border border-slate-200">
                        <ChatCircleText size={11} weight="fill" /> {noteCounts[r.schedule_id]}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-navy/65">{r.contact}</div>
                </Td>
                <Td className="max-w-[280px]">
                  <div className="text-navy/80 truncate" title={r.description}>{r.description || "—"}</div>
                </Td>
                <Td><StatusBadge value={r.category} /></Td>
                {cols.standard && <Td><StandardBadge value={r.compliance} testid={`register-standard-${r.schedule_id}`} /></Td>}
                {cols.bs_code && <Td mono className="text-navy/70">{r.source_account_code || "—"}</Td>}
                {cols.bs_account && <Td className="text-navy/70 max-w-[180px]"><span className="truncate block" title={r.source_account_name}>{r.source_account_name || "—"}</span></Td>}
                {cols.pl_code && <Td mono className="text-navy/70">{r.pl_account_code || "—"}</Td>}
                {cols.pl_account && <Td className="text-navy/70 max-w-[180px]"><span className="truncate block" title={r.pl_account_name}>{r.pl_account_name || "—"}</span></Td>}
                <Td right mono className="font-semibold">{money(r.original_amount, r.currency)}</Td>
                <Td right mono className="text-positive">{money(r.released_amount, r.currency)}</Td>
                <Td right mono className="font-semibold">{money(r.remaining_balance, r.currency)}</Td>
                {cols.next_journal && <Td mono>{fmtDate(r.next_journal_date)}</Td>}
                {cols.final_date && <Td mono>{fmtDate(r.final_date)}</Td>}
                <Td><StatusBadge value={r.status} testid={`status-${r.schedule_id}`} /></Td>
                <Td right>
                  <div className="flex items-center justify-end gap-2">
                    {r.status !== "Completed" && (
                      <button onClick={(e) => openRelease(e, r)} data-testid={`row-release-full-${r.schedule_id}`}
                        title="Release remaining balance in full"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-orange/40 text-orangeink hover:bg-orange hover:text-white transition-colors whitespace-nowrap">
                        <Lightning size={14} weight="fill" /> Release in full
                      </button>
                    )}
                    {r.posted_count === 0 && (
                      <button onClick={(e) => openDelete(e, r)} data-testid={`row-delete-${r.schedule_id}`}
                        title="Delete this line (nothing has been posted to Xero)"
                        className="inline-flex items-center justify-center p-1.5 rounded-lg border border-negative/30 text-negative/70 hover:bg-negative hover:text-white transition-colors">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </Td>
              </tr>
            );
            if (!groupByAccount) return filtered.map(renderRow);
            return accountGroups.flatMap((grp) => [
              <tr key={`grp-${grp.code}`} data-testid={`register-group-${grp.code}`} className="bg-slate-100/70 border-y border-slate-200">
                <td colSpan={totalCols} className="px-5 py-2.5">
                  <span className="overline text-navy/70 font-semibold">{grp.code}{grp.name && grp.name !== grp.code ? ` · ${grp.name}` : ""}</span>
                  <span className="ml-2 text-xs text-navy/50">{grp.rows.length} schedule{grp.rows.length === 1 ? "" : "s"}</span>
                </td>
              </tr>,
              ...grp.rows.map(renderRow),
              <tr key={`sub-${grp.code}`} data-testid={`register-subtotal-${grp.code}`} className="bg-slate-50 border-b border-slate-200 font-semibold">
                <td colSpan={leadCols} className="px-5 py-2.5 text-right overline text-navy/60">{grp.code} subtotal</td>
                <td className="px-5 py-2.5 text-right font-mono tabular text-navy" data-testid={`register-subtotal-original-${grp.code}`}>{money(grp.original, grp.ccy)}</td>
                <td className="px-5 py-2.5 text-right font-mono tabular text-positive" data-testid={`register-subtotal-released-${grp.code}`}>{money(grp.released, grp.ccy)}</td>
                <td className="px-5 py-2.5 text-right font-mono tabular text-navy" data-testid={`register-subtotal-remaining-${grp.code}`}>{money(grp.remaining, grp.ccy)}</td>
                <td colSpan={trailCols + 1} />
              </tr>,
            ]);
            })()}
          </tbody>
          {!loading && filtered.length > 0 && (
            <tfoot>
              <tr className="bg-navy font-semibold text-white" data-testid="register-totals-row">
                <td className="px-5 py-4 text-left overline text-white/70" colSpan={leadCols}>
                  Totals · {filtered.length} schedule{filtered.length === 1 ? "" : "s"}
                </td>
                <td className="px-5 py-4 text-right font-mono tabular text-white" data-testid="register-total-original">{money(totals.original, totalsCcy)}</td>
                <td className="px-5 py-4 text-right font-mono tabular text-[#5fd39b]" data-testid="register-total-released">{money(totals.released, totalsCcy)}</td>
                <td className="px-5 py-4 text-right font-mono tabular text-white" data-testid="register-total-remaining">{money(totals.remaining, totalsCcy)}</td>
                <td colSpan={trailCols + 1} />
              </tr>
            </tfoot>
          )}
        </table>
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-navy/70" data-testid="register-empty">
            {rows.length === 0 ? (
              <>
                <div className="font-heading font-bold text-lg text-navy">No schedules yet</div>
                <p className="text-sm mt-1">Create a release schedule from an unscheduled item.</p>
              </>
            ) : (
              <>
                <div className="font-heading font-bold text-lg text-navy">No matching items</div>
                <p className="text-sm mt-1">Try a different search term or clear your filters.</p>
              </>
            )}
          </div>
        )}
        {loading && <div className="py-16 text-center font-mono text-slate-600 text-sm tracking-widest">LOADING_DATA…</div>}
      </div>

      {detail && <ScheduleDrawer detail={detail} onClose={() => setDetail(null)} onAction={() => { setDetail(null); load(); }} />}
      <CreateAdjustmentModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />

      <RunPostingDialog open={showRun} onClose={() => setShowRun(false)} onConfirm={runDaily} running={running} />

      <Dialog open={!!releaseTarget} onOpenChange={(o) => !o && setReleaseTarget(null)}>
        <DialogContent className="max-w-md rounded-lg border-slate-200" data-testid="quick-release-dialog">
          <DialogHeader>
            <div className="overline text-orangeink mb-1">Release in full</div>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">
              {releaseTarget?.doc_number} · {releaseTarget?.contact}
            </DialogTitle>
            <DialogDescription className="text-navy/70">
              Release the remaining balance in full on a chosen date.
            </DialogDescription>
          </DialogHeader>
          {releaseTarget && (
            <div className="space-y-4">
              <div className="flex items-center justify-between bg-navy/[0.03] border border-slate-200 rounded-lg px-4 py-3">
                <span className="overline text-navy/65">Remaining balance</span>
                <span className="font-mono font-semibold text-navy tabular">{money(releaseTarget.remaining_balance, releaseTarget.currency)}</span>
              </div>
              <p className="text-xs text-navy/70">
                Posts the entire remaining balance as a single journal on the chosen date and cancels the
                remaining scheduled (pending) journals. Already-posted journals are untouched.
              </p>
              <div>
                <span className="overline text-navy/70 block mb-1.5">Release date</span>
                <input type="date" value={releaseDate} data-testid="quick-release-date"
                  onChange={(e) => setReleaseDate(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setReleaseTarget(null)} data-testid="quick-release-cancel"
                  className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
                <button onClick={confirmRelease} disabled={releaseBusy} data-testid="quick-release-confirm"
                  className="px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                  {releaseBusy ? "SCHEDULING…" : `Release ${money(releaseTarget.remaining_balance, releaseTarget.currency)}`}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md rounded-lg border-slate-200" data-testid="delete-line-dialog">
          <DialogHeader>
            <div className="overline text-negative mb-1">Delete line</div>
            <DialogTitle className="font-heading font-bold text-xl tracking-tight text-navy">
              {deleteTarget?.doc_number} · {deleteTarget?.contact}
            </DialogTitle>
            <DialogDescription className="text-navy/70">
              Permanently remove this {deleteTarget?.manual ? "manual adjustment" : "line"} from the register.
            </DialogDescription>
          </DialogHeader>
          {deleteTarget && ((deleteTarget.released_amount || 0) > 0 ? (
            <div className="space-y-4" data-testid="delete-reverse-panel">
              <div className="rounded-lg border border-orange/40 bg-orange/[0.06] px-4 py-3 text-sm text-navy">
                <b>This line has already posted journals to Xero</b> ({money(deleteTarget.released_amount, deleteTarget.currency)} released).
                It can't be deleted cleanly, as that would leave Xero out of step with the register.
                Instead, WWL can post <b>reversing journals</b> to Xero to back out what was posted, then remove the line —
                so Xero matches the register.
              </div>
              <p className="text-xs text-navy/70">Reversing journals are dated today. Any pending (scheduled) journals will be cancelled.</p>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setDeleteTarget(null)} data-testid="delete-line-cancel"
                  className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
                <button onClick={doReverse} disabled={deleteBusy} data-testid="delete-reverse-confirm"
                  className="px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                  {deleteBusy ? "REVERSING…" : "Reverse in Xero & remove line"}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <b>Nothing has been posted to Xero</b> for this line, so your Xero balances are unaffected.
                Any pending (scheduled) journals will be cancelled.
                {deleteTarget.manual
                  ? " This adjustment was created in-app and will be removed completely."
                  : " As this came from an imported item, it will be returned to the Unscheduled queue."}
              </div>
              <p className="text-xs text-navy/70">
                If journals had already been posted to Xero, deletion is blocked — you'd need to open the line
                and use “Reverse in Xero” so reversing journals keep Xero in step.
              </p>
              <div className="flex justify-end gap-3 pt-1">
                <button onClick={() => setDeleteTarget(null)} data-testid="delete-line-cancel"
                  className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
                <button onClick={confirmDelete} disabled={deleteBusy} data-testid="delete-line-confirm"
                  className="px-5 py-2.5 text-sm font-medium rounded-lg bg-negative text-white hover:opacity-90 transition-opacity disabled:opacity-60">
                  {deleteBusy ? "DELETING…" : "Delete line"}
                </button>
              </div>
            </div>
          ))}
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function RunPostingDialog({ open, onClose, onConfirm, running }) {
  const [data, setData] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get("/reports/journals-to-post").then(({ data }) => setData(data)).catch(() => setData(null)),
      api.get("/scheduler/last-run").then(({ data }) => setLastRun(data)).catch(() => setLastRun(null)),
    ]).finally(() => setLoading(false));
  }, [open]);

  const fmtWhen = (iso) => {
    if (!iso) return "Never run yet";
    try { return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const count = data?.count || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="run-posting-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl text-navy">Run daily posting</DialogTitle>
          <DialogDescription className="text-navy/70">
            This posts all due journals up to the open close-period cut-off{data?.cutoff ? ` (to ${fmtDate(data.cutoff)})` : ""} — future periods are held. Journals go to Approvals first if 2-person approval is on.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5 text-sm" data-testid="run-last-run">
          <span className="text-navy/70">Last run</span>
          <span className="font-medium text-navy">{fmtWhen(lastRun?.last_run)}
            {lastRun?.last_result ? <span className="text-navy/65 font-normal"> · posted {lastRun.last_result.posted}{lastRun.last_result.queued ? `, queued ${lastRun.last_result.queued}` : ""}</span> : null}</span>
        </div>

        <div className="mt-3 border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[0.62rem] uppercase tracking-wider text-navy/65">
                <th className="px-3 py-2.5 text-left">Date</th><th className="px-3 py-2.5 text-left">Description</th>
                <th className="px-3 py-2.5 text-left">Code</th><th className="px-3 py-2.5 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="py-8 text-center font-mono text-navy/65 text-sm">LOADING…</td></tr>}
              {!loading && (data?.journals || []).map((j, i) => (
                <tr key={i} data-testid={`run-row-${i}`} className="border-b border-slate-100 last:border-0">
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{fmtDate(j.date)}</td>
                  <td className="px-3 py-2.5 max-w-[240px] truncate" title={j.description}>{j.description}
                    {j.post_error ? <div className="text-xs text-negative mt-0.5">⚠ {j.post_error}</div> : null}</td>
                  <td className="px-3 py-2.5 font-mono text-xs text-navy/70">{j.source_account_code}{j.pl_account_code ? ` → ${j.pl_account_code}` : ""}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold tabular">{money(j.amount)}</td>
                </tr>
              ))}
              {!loading && count === 0 && (
                <tr><td colSpan={4} className="py-8 text-center text-navy/70" data-testid="run-nothing">Nothing due to post right now.</td></tr>
              )}
            </tbody>
            {!loading && count > 0 && (
              <tfoot>
                <tr className="border-t-2 border-navy/20 font-semibold text-navy">
                  <td className="px-3 py-2.5" colSpan={3}>Total · {count} journal(s)</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular" data-testid="run-total">{money(data.total)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <button onClick={onClose} className="px-5 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
          <button onClick={onConfirm} disabled={running || loading || count === 0} data-testid="run-confirm-btn"
            className="inline-flex items-center gap-1.5 px-6 py-2 text-sm font-medium rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-60">
            <Play size={15} weight="fill" /> {running ? "POSTING…" : `Post ${count || ""} now`}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ScheduleDrawer({ detail, onClose, onAction }) {
  const s = detail.schedule;
  const remaining = Math.round((s.original_amount - (s.released_amount || 0)) * 100) / 100;
  const [showRelease, setShowRelease] = useState(false);
  const [releaseDate, setReleaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmHardDel, setConfirmHardDel] = useState(false);
  const [reverseDate, setReverseDate] = useState(new Date().toISOString().slice(0, 10));
  const [showExtend, setShowExtend] = useState(false);
  const [extendDate, setExtendDate] = useState("");
  const [form, setForm] = useState({
    description: s.description || "", pl_account_code: s.pl_account_code || "",
    original_amount: s.original_amount, start_date: s.start_date, end_date: s.end_date,
    frequency: (s.frequency || "Monthly"),
    release_basis: (s.release_basis || "monthly"),
    rounding: (s.rounding || "last"),
  });
  const [plAccounts, setPlAccounts] = useState([]);
  const [editSplit, setEditSplit] = useState({
    enabled: (s.pl_split || []).length >= 2, valid: true, pl_split: s.pl_split || [],
  });
  useEffect(() => {
    if (!editing) return;
    const plClass = s.category === "DEFERRED_INCOME" ? "REVENUE" : "EXPENSE";
    api.get("/accounts").then(({ data }) => {
      setPlAccounts((data.accounts || []).filter((a) => a.class === plClass));
    }).catch(() => {});
  }, [editing, s.category]);
  const plOptions = (() => {
    const list = [...plAccounts];
    if (form.pl_account_code && !list.find((a) => a.code === form.pl_account_code)) {
      list.unshift({ code: form.pl_account_code, name: s.pl_account_name || "Current code" });
    }
    return list;
  })();

  const releaseFull = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedules/${s.id}/release-full`, { date: releaseDate });
      toast.success(`Full release of ${money(data.remaining, s.currency)} scheduled for ${fmtDate(data.date)}`);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule full release");
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (editSplit.enabled && !editSplit.valid) { toast.error("Fix the P&L split — codes must be unique and total 100%"); return; }
    setBusy(true);
    try {
      const payload = {
        description: form.description, pl_account_code: form.pl_account_code,
        original_amount: Number(form.original_amount), start_date: form.start_date,
        end_date: form.end_date, frequency: form.frequency, release_basis: form.release_basis,
        rounding: form.rounding,
        pl_split: editSplit.enabled ? editSplit.pl_split : [],
      };
      await api.patch(`/schedules/${s.id}`, payload);
      toast.success("Schedule updated");
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update schedule");
    } finally { setBusy(false); }
  };

  const reverse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/schedules/${s.id}/reverse`, { date: reverseDate });
      toast.success(data.reversed
        ? `Posted ${data.reversed} reversing journal(s) to Xero and removed the line.`
        : "Line removed (nothing was posted to Xero).");
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reverse in Xero");
    } finally { setBusy(false); }
  };

  const delLine = async () => {
    setBusy(true);
    try {
      await api.delete(`/schedules/${s.id}`);
      toast.success(s.manual
        ? "Manual adjustment deleted."
        : "Line deleted — the imported item was returned to the Unscheduled queue.");
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete this line");
    } finally { setBusy(false); }
  };

  const lifecycle = async (action) => {
    setBusy(true);
    try {
      await api.post(`/schedules/${s.id}/${action}`);
      toast.success({ pause: "Schedule paused", resume: "Schedule resumed", cancel: "Schedule cancelled" }[action]);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not ${action} schedule`);
    } finally { setBusy(false); }
  };

  const extend = async () => {
    if (!extendDate) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/schedules/${s.id}/extend`, { end_date: extendDate });
      toast.success(`Extended — remaining redistributed over ${data.new_periods} period(s)`);
      onAction?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not extend schedule");
    } finally { setBusy(false); }
  };

  const postedCount = (detail.journals || []).filter(
    (j) => j.status === "Posted" && j.xero_journal_id && j.xero_journal_id !== "OPENING-BALANCE").length;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" data-testid="schedule-detail-drawer">
      <div className="absolute inset-0 bg-navy/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl fade-up">
        <div className="flex items-start justify-between px-4 sm:px-7 py-4 sm:py-6 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div>
            <div className="overline text-orangeink mb-1">Schedule detail</div>
            <div className="font-heading font-bold text-lg sm:text-xl tracking-tight text-navy">{s.doc_number} · {s.contact}</div>
            <div className="text-sm text-navy/70 mt-1">{s.description}</div>
            <span data-testid="drawer-status-badge" className={`inline-block mt-2 text-[0.62rem] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${
              s.status === "Paused" ? "bg-warn/15 text-warn"
              : s.status === "Cancelled" ? "bg-navy/10 text-navy/70 line-through"
              : s.status === "Completed" ? "bg-positive/10 text-positive"
              : "bg-orange/10 text-orangeink"}`}>{s.status}</span>
          </div>
          <button onClick={onClose} data-testid="close-drawer-btn" className="text-slate-600 hover:text-navy"><X size={22} /></button>
        </div>

        <div className="px-4 sm:px-7 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          {s.xero_url && (
            <XeroLink href={s.xero_url} size="md" testId="view-in-xero-btn" title="View in Xero" />
          )}
          <ExplainMovement
            endpoint={`/explain/schedule/${s.id}`}
            title={`${s.doc_number} · ${s.contact}`}
            subtitle="What this line is and how it's moving, in plain English."
            testid="schedule-explain" size="md" />
          <button onClick={() => setEditing(!editing)} data-testid="edit-schedule-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
            <PencilSimple size={14} /> {editing ? "Close edit" : "Edit line"}
          </button>
          <button onClick={() => setConfirmDel(true)} data-testid="reverse-schedule-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors">
            <ArrowUUpLeft size={14} /> Reverse in Xero
          </button>
          {postedCount === 0 && (
            <button onClick={() => setConfirmHardDel(true)} data-testid="delete-schedule-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-negative/40 text-negative hover:bg-negative hover:text-white transition-colors">
              <Trash size={14} /> Delete
            </button>
          )}
          {s.status !== "Cancelled" && (
            s.status === "Paused" ? (
              <button onClick={() => lifecycle("resume")} disabled={busy} data-testid="resume-schedule-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-positive/40 text-positive hover:bg-positive/10 transition-colors">
                <Play size={14} weight="fill" /> Resume
              </button>
            ) : (
              <button onClick={() => lifecycle("pause")} disabled={busy} data-testid="pause-schedule-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">
                <Pause size={14} weight="fill" /> Pause
              </button>
            )
          )}
          {s.status !== "Cancelled" && (
            <button onClick={() => lifecycle("cancel")} disabled={busy} data-testid="cancel-schedule-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-warn/40 text-warn hover:bg-warn/10 transition-colors">
              <ProhibitInset size={14} /> Cancel
            </button>
          )}
          {s.status !== "Cancelled" && s.status !== "Completed" && (
            <button onClick={() => { setShowExtend(!showExtend); setExtendDate(s.end_date?.slice(0, 10) || ""); }} disabled={busy} data-testid="extend-schedule-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange/40 text-orangeink hover:bg-orange/10 transition-colors">
              <Play size={14} /> Extend
            </button>
          )}
        </div>

        {showExtend && (
          <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-orange/[0.03] flex flex-wrap items-end gap-3" data-testid="extend-panel">
            <Lbl t="New end date">
              <input type="date" value={extendDate} onChange={(e) => setExtendDate(e.target.value)} data-testid="extend-date-input" className="ed-in font-mono" />
            </Lbl>
            <button onClick={extend} disabled={busy} data-testid="extend-apply-btn"
              className="px-4 py-2 text-sm font-medium rounded-lg bg-navy text-white hover:bg-orange transition-colors disabled:opacity-60">Apply</button>
            <span className="text-xs text-navy/70 pb-2">Posted journals stay; the remaining balance is spread over the longer term.</span>
          </div>
        )}

        {editing && (
          <div className="px-4 sm:px-7 py-5 border-b border-slate-100 bg-navy/[0.02] space-y-3" data-testid="edit-schedule-panel">
            <p className="text-xs text-navy/70">
              Amounts, dates and frequency can only be changed before any journal posts to Xero; otherwise edit
              the description / P&amp;L code only, or delete and recreate the line.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Lbl t="Description"><input value={form.description} data-testid="edit-description"
                onChange={(e) => setForm({ ...form, description: e.target.value })} className="ed-in" /></Lbl>
              <Lbl t="P&L account code">
                <Select value={form.pl_account_code} onValueChange={(v) => setForm({ ...form, pl_account_code: v })}>
                  <SelectTrigger data-testid="edit-pl-code" className="ed-in"><SelectValue placeholder="Select P&L code" /></SelectTrigger>
                  <SelectContent>
                    {plOptions.length === 0 && <div className="px-3 py-2 text-xs text-navy/70">Loading codes…</div>}
                    {plOptions.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Lbl>
              <Lbl t="Original amount"><input type="number" value={form.original_amount} data-testid="edit-amount"
                onChange={(e) => setForm({ ...form, original_amount: e.target.value })} className="ed-in font-mono" /></Lbl>
              <Lbl t="Frequency">
                <select value={form.frequency} data-testid="edit-frequency"
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="ed-in">
                  {["Monthly", "Quarterly", "Annual", "Weekly", "Custom", "Full"].map((f) => <option key={f}>{f}</option>)}
                </select></Lbl>
              <Lbl t="Start date"><input type="date" value={form.start_date} data-testid="edit-start"
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="ed-in font-mono" /></Lbl>
              <Lbl t="End date"><input type="date" value={form.end_date} data-testid="edit-end"
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="ed-in font-mono" /></Lbl>
              <Lbl t="Release basis">
                <select value={form.release_basis} data-testid="edit-release-basis"
                  onChange={(e) => setForm({ ...form, release_basis: e.target.value })} className="ed-in">
                  <option value="monthly">Even periods</option>
                  <option value="daily">Daily pro-rata</option>
                </select></Lbl>
              {form.release_basis !== "daily" && (
                <Lbl t="Rounding on">
                  <select value={form.rounding} data-testid="edit-rounding"
                    onChange={(e) => setForm({ ...form, rounding: e.target.value })} className="ed-in">
                    <option value="last">Last period</option>
                    <option value="first">First period</option>
                  </select></Lbl>
              )}
            </div>
            <PLSplitEditor plAccounts={plAccounts} total={Number(form.original_amount) || 0}
              primaryCode={form.pl_account_code} initialSplit={s.pl_split} onChange={setEditSplit} />
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
              <button onClick={saveEdit} disabled={busy} data-testid="save-schedule-btn"
                className="px-5 py-2 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                {busy ? "SAVING…" : "Save changes"}
              </button>
            </div>
            <style>{`.ed-in{width:100%;background:#fff;border:1px solid rgba(11,26,47,.2);border-radius:4px;padding:.5rem .65rem;font-size:.85rem;color:#0E0F3F;outline:none}.ed-in:focus{border-color:#F37A24;box-shadow:0 0 0 2px rgba(46,163,253,.25)}`}</style>
          </div>
        )}

        {confirmDel && (
          <div className="px-4 sm:px-7 py-4 border-b border-negative/20 bg-negative/[0.04]" data-testid="reverse-confirm">
            <p className="text-sm text-navy mb-3">
              Reverse <b>{s.doc_number} · {s.contact}</b> and remove the line?
              {postedCount > 0
                ? <> This posts <b>{postedCount}</b> reversing Manual Journal(s) to Xero to back out everything this line posted, then removes it from the register.</>
                : <> Nothing has been posted to Xero yet, so this just cancels the pending journals and removes the line.</>}
            </p>
            {postedCount > 0 && (
              <div className="mb-3">
                <span className="overline text-navy/70 block mb-1.5">Reversal date</span>
                <input type="date" value={reverseDate} data-testid="reverse-date"
                  onChange={(e) => setReverseDate(e.target.value)}
                  className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(false)} data-testid="reverse-cancel" className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Keep</button>
              <button onClick={reverse} disabled={busy} data-testid="reverse-confirm-btn"
                className="px-5 py-2 text-sm font-medium rounded-lg bg-negative text-white hover:opacity-90 transition-opacity disabled:opacity-60">
                {busy ? "REVERSING…" : (postedCount > 0 ? "Reverse in Xero" : "Remove line")}
              </button>
            </div>
          </div>
        )}

        {confirmHardDel && (
          <div className="px-4 sm:px-7 py-4 border-b border-negative/20 bg-negative/[0.04]" data-testid="delete-confirm">
            <p className="text-sm text-navy mb-3">
              Delete <b>{s.doc_number} · {s.contact}</b>?
              <> Nothing has been posted to Xero for this line, so your Xero balances are unaffected — this
              cancels any pending journals and {s.manual ? "removes the manual adjustment completely" : "returns the imported item to the Unscheduled queue"}.</>
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmHardDel(false)} data-testid="delete-cancel" className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Keep</button>
              <button onClick={delLine} disabled={busy} data-testid="delete-confirm-btn"
                className="px-5 py-2 text-sm font-medium rounded-lg bg-negative text-white hover:opacity-90 transition-opacity disabled:opacity-60">
                {busy ? "DELETING…" : "Delete line"}
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-px bg-navy/10 border-b border-slate-100">
          <Meta label="Category" value={s.category} />
          <Meta label="Frequency" value={s.frequency} sub={s.release_basis === "daily" ? "Daily pro-rata" : "Even periods"} />
          <Meta label="Compliance standard" value={STD_LABEL[s.compliance] || (s.compliance || "Untagged")} sub="From the BS code" />
          <Meta label="Balance Sheet Code" value={s.source_account_code} sub={s.source_account_name} />
          <Meta label="P&L Code" value={s.pl_split?.length >= 2 ? `Split · ${s.pl_split.length} codes` : s.pl_account_code} sub={s.pl_split?.length >= 2 ? "See split below" : s.pl_account_name} />
          <Meta label="Original" value={money(s.original_amount, s.currency)} />
          <Meta label="Released" value={money(s.released_amount, s.currency)} />
          <div className="bg-white" aria-hidden="true" />
        </div>

        {s.pl_split?.length >= 2 && (
          <div className="px-4 sm:px-7 py-3 border-b border-slate-100" data-testid="pl-split-detail">
            <div className="overline text-navy/65 mb-2">P&amp;L split — each release is apportioned as</div>
            <div className="flex flex-wrap gap-2">
              {s.pl_split.map((sp, i) => (
                <span key={i} className="text-xs font-mono px-2.5 py-1 bg-orange/10 text-orangeink rounded-md border border-orange/20">
                  {sp.pl_account_code}{sp.pl_account_name ? ` ${sp.pl_account_name}` : ""} · {Number(sp.percent).toFixed(1)}%
                  <span className="text-navy/60"> ({money((s.original_amount || 0) * sp.percent / 100, s.currency)})</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {s.tracking?.length > 0 && (
          <div className="px-4 sm:px-7 py-3 border-b border-slate-100 flex flex-wrap gap-2">
            {s.tracking.map((t, i) => (
              <span key={i} className="text-xs font-mono px-2 py-1 bg-navy/5 rounded-md border border-slate-200">{t.name}: {t.option}</span>
            ))}
          </div>
        )}

        {remaining > 0.0049 && (
          <div className="px-4 sm:px-7 py-4 border-b border-slate-100 bg-navy/[0.02]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="overline text-navy/65">Remaining balance</div>
                <div className="font-mono font-semibold text-navy tabular">{money(remaining, s.currency)}</div>
              </div>
              {!showRelease && (
                <button onClick={() => setShowRelease(true)} data-testid="open-release-full-btn"
                  className="flex items-center gap-2 bg-white border border-orange/40 text-orangeink px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange hover:text-white transition-colors">
                  <Lightning size={16} weight="fill" /> Release in full
                </button>
              )}
            </div>
            {showRelease && (
              <div className="mt-4 bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-xs text-navy/70 mb-3">
                  Posts the entire remaining balance as a single journal on the chosen date and cancels the
                  remaining scheduled (pending) journals. Already-posted journals are untouched.
                </p>
                <div className="flex items-end gap-3 flex-wrap">
                  <div>
                    <span className="overline text-navy/70 block mb-1.5">Release date</span>
                    <input type="date" value={releaseDate} data-testid="release-full-date"
                      onChange={(e) => setReleaseDate(e.target.value)}
                      className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
                  </div>
                  <button onClick={releaseFull} disabled={busy} data-testid="confirm-release-full-btn"
                    className="px-5 py-2.5 text-sm font-medium rounded-lg bg-orange text-white hover:bg-navy transition-colors disabled:opacity-60">
                    {busy ? "SCHEDULING…" : `Release ${money(remaining, s.currency)}`}
                  </button>
                  <button onClick={() => setShowRelease(false)} data-testid="cancel-release-full-btn"
                    className="px-4 py-2.5 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 transition-colors">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-4 sm:px-7 py-5">
          <div className="overline text-navy/65 mb-3">Journals</div>
          <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono tabular">
            <thead className="text-navy/65 overline">
              <tr><th className="text-left py-1.5">#</th><th className="text-left">Date</th><th className="text-right">Amount</th><th className="text-left pl-3">Status</th><th className="text-left pl-3">Xero</th></tr>
            </thead>
            <tbody>
              {detail.journals.map((j) => (
                <tr key={j.id} className="border-t border-slate-100" data-testid={`journal-row-${j.period}`}>
                  <td className="py-2 text-navy/70">{j.period}</td>
                  <td>{fmtDate(j.date)}</td>
                  <td className="text-right">{money(j.amount, s.currency)}</td>
                  <td className="pl-3"><StatusBadge value={j.status} /></td>
                  <td className="text-navy/70 pl-3">
                    {j.xero_url
                      ? <XeroLink href={j.xero_url} size="sm" testId={`journal-xero-link-${j.period}`} title="View this journal in Xero" />
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <div className="px-4 sm:px-7 py-5 border-t border-slate-100 bg-navy/[0.015]">
          <ReviewNotes entityType="schedule" entityId={s.id} onChange={onAction} />
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, sub }) {
  return (
    <div className="bg-white px-4 sm:px-7 py-3 flex flex-col min-h-[78px]">
      <div className="overline text-slate-600">{label}</div>
      <div className="font-mono text-sm text-navy mt-1 tabular break-words">{value}</div>
      <div className="text-xs text-navy/70 mt-0.5 leading-snug min-h-[16px]">{sub || ""}</div>
    </div>
  );
}

function Lbl({ t, children }) {
  return (
    <label className="block">
      <span className="overline text-navy/70 block mb-1">{t}</span>
      {children}
    </label>
  );
}

const Th = ({ children, right }) => <th className={`px-5 py-3 ${right ? "text-right" : "text-left"} font-semibold whitespace-nowrap text-[0.66rem] uppercase tracking-wider text-slate-600 bg-slate-50`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) =>
  <td className={`px-5 py-3.5 ${right ? "text-right" : "text-left"} ${mono ? "font-mono tabular" : ""} ${className}`}>{children}</td>;
